import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ordersApi } from '@/lib/api/orders';
import { useDialog } from '@/hooks/useDialog';
import { Dialog } from '@/components/ui/dialog';
import { useUndoHistory } from '@/contexts/undo-history-context';
import type { Order, OrderStatus } from '@/types/order';

export function OrdersPage() {
  const navigate = useNavigate();
  const { dialog, closeDialog, showError, showConfirm } = useDialog();
  const { push } = useUndoHistory();
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<OrderStatus | 'all'>('Open');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const loadOrders = useCallback(async () => {
    try {
      const statusFilter = activeTab === 'all' ? undefined : activeTab;
      const data = await ordersApi.getAll(statusFilter, sortBy || undefined, sortDirection || undefined);
      setOrders(data ?? []);
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setIsLoading(false);
    }
  }, [activeTab, sortBy, sortDirection]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      loadOrders();
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(interval);
  }, [autoRefresh, loadOrders]);

  const handleUnlock = async (id: number) => {
    showConfirm(
      'Unlock Order',
      'Are you sure you want to unlock this order?',
      async () => {
        try {
          await ordersApi.unlock(id);
          loadOrders();
        } catch (error) {
          console.error('Error unlocking order:', error);
          showError('Error', 'Failed to unlock order');
        }
      },
      'Unlock'
    );
  };

  const handleDelete = async (id: number) => {
    const order = orders.find((o) => o.id === id);
    showConfirm(
      'Delete Order',
      'Delete this order? You can undo this from the history menu.',
      async () => {
        try {
          await ordersApi.delete(id);
          push(
            `Order #${order?.orderNumber ?? id}`,
            () => ordersApi.restore(id),
            order ? `${order.customerName} · Truck ${order.truckNumber}` : undefined,
            loadOrders
          );
          loadOrders();
        } catch (error) {
          const message = (error as { response?: { data?: { message?: string } } }).response?.data?.message || 'Failed to delete order';
          showError('Error', message);
        }
      },
      'Delete'
    );
  };

  const handleHold = async (id: number) => {
    showConfirm(
      'Hold Order',
      'Put this order on hold? It will be removed from the mobile picking queue.',
      async () => {
        try {
          await ordersApi.hold(id);
          loadOrders();
        } catch (error) {
          console.error('Error holding order:', error);
          showError('Error', 'Failed to hold order');
        }
      },
      'Hold'
    );
  };

  const handleResume = async (id: number) => {
    showConfirm(
      'Resume Order',
      'Resume this order? It will return to the picking queue.',
      async () => {
        try {
          await ordersApi.resume(id);
          loadOrders();
        } catch (error) {
          console.error('Error resuming order:', error);
          showError('Error', 'Failed to resume order');
        }
      },
      'Resume'
    );
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  };

  const getSortIndicator = (column: string) => {
    if (sortBy !== column) return '';
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  };

  const getStatusColor = (status: OrderStatus) => {
    switch (status) {
      case 'Open':
        return 'bg-blue-900 text-blue-200';
      case 'InProgress':
        return 'bg-yellow-900 text-yellow-200';
      case 'Complete':
        return 'bg-green-900 text-green-200';
      case 'Cancelled':
        return 'bg-red-900 text-red-200';
      case 'OnHold':
        return 'bg-orange-900 text-orange-200';
      default:
        return 'bg-gray-700 text-gray-400';
    }
  };

  const tabs: Array<{ value: OrderStatus | 'all'; label: string }> = [
    { value: 'Open', label: 'Open' },
    { value: 'InProgress', label: 'In Progress' },
    { value: 'OnHold', label: 'On Hold' },
    { value: 'Complete', label: 'Complete' },
    { value: 'all', label: 'All' },
  ];

  // Filter orders based on search query
  const filteredOrders = orders.filter(order => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      (order.orderNumber ?? '').toLowerCase().includes(query) ||
      order.truckNumber.toLowerCase().includes(query) ||
      order.customerName.toLowerCase().includes(query)
    );
  });

  if (isLoading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Order Management</h1>
        <div className="flex items-center gap-4">
          <label className="flex items-center text-sm">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="mr-2"
            />
            Auto-refresh (30s)
          </label>
          <Link
            to="/orders/create"
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
          >
            Create Order
          </Link>
        </div>
      </div>

      {/* Search */}
      <div className="bg-gray-800 rounded-lg p-4">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-blue-500"
          placeholder="Search by order number, truck, or customer..."
        />
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-700 pb-2">
        {tabs.map(({ value, label }) => (
          <button
            key={value}
            onClick={() => setActiveTab(value)}
            className={`px-4 py-2 text-sm rounded-t transition-colors ${
              activeTab === value
                ? 'bg-gray-800 text-white border-b-2 border-blue-500'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Orders Table */}
      <div className="bg-gray-800 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-700">
            <tr>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase cursor-pointer hover:text-white select-none"
                onClick={() => handleSort('orderNumber')}
              >
                Release #{getSortIndicator('orderNumber')}
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase cursor-pointer hover:text-white select-none"
                onClick={() => handleSort('truckNumber')}
              >
                Truck{getSortIndicator('truckNumber')}
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase cursor-pointer hover:text-white select-none"
                onClick={() => handleSort('customerName')}
              >
                Customer{getSortIndicator('customerName')}
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase cursor-pointer hover:text-white select-none"
                onClick={() => handleSort('status')}
              >
                Status{getSortIndicator('status')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">
                Progress
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">
                Claimed By
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase cursor-pointer hover:text-white select-none"
                onClick={() => handleSort('createdAt')}
              >
                Created{getSortIndicator('createdAt')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {filteredOrders.map((order) => (
              <tr
                key={order.id}
                onClick={() => navigate(`/orders/${order.id}`)}
                className="hover:bg-gray-750 cursor-pointer"
              >
                <td className="px-6 py-4 font-semibold text-blue-400">
                  {order.orderNumber ?? '—'}
                </td>
                <td className="px-6 py-4">{order.truckNumber}</td>
                <td className="px-6 py-4">{order.customerName}</td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 text-xs rounded ${getStatusColor(order.status)}`}>
                      {order.status}
                    </span>
                    {order.isOpenOrder && (
                      <span className="px-2 py-1 text-xs rounded bg-purple-900 text-purple-200 font-medium">
                        Open Order
                      </span>
                    )}
                    {order.productName && (
                      <span className="text-xs text-gray-400" title="Product Name">
                        {order.productName}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-gray-700 rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-green-500 h-full transition-all"
                        style={{
                          width: `${order.totalItems > 0 ? (order.pickedItems / order.totalItems) * 100 : 0}%`,
                        }}
                      />
                    </div>
                    <span className="text-sm text-gray-400 whitespace-nowrap">
                      {order.pickedItems}/{order.totalItems}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  {order.claimedByName ? (
                    <span className="text-yellow-400">{order.claimedByName}</span>
                  ) : (
                    <span className="text-gray-500">-</span>
                  )}
                </td>
                <td className="px-6 py-4 text-sm text-gray-400">
                  {new Date(order.createdAt).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 space-x-2">
                  {(() => {
                    const showUnlock = order.claimedById && order.status !== 'Complete';
                    const showDelete = order.status === 'Open';
                    const showHold = order.status === 'Open' || order.status === 'InProgress';
                    const showResume = order.status === 'OnHold';
                    const hasActions = showUnlock || showDelete || showHold || showResume;

                    return (
                      <>
                        {showHold && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleHold(order.id);
                            }}
                            className="text-orange-400 hover:text-orange-300"
                          >
                            Hold
                          </button>
                        )}
                        {showResume && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleResume(order.id);
                            }}
                            className="text-green-400 hover:text-green-300"
                          >
                            Resume
                          </button>
                        )}
                        {showUnlock && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUnlock(order.id);
                            }}
                            className="text-yellow-400 hover:text-yellow-300"
                          >
                            Unlock
                          </button>
                        )}
                        {showDelete && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(order.id);
                            }}
                            className="text-red-400 hover:text-red-300"
                          >
                            Delete
                          </button>
                        )}
                        {!hasActions && <span className="text-gray-500">-</span>}
                      </>
                    );
                  })()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredOrders.length === 0 && (
          <div className="text-center py-8 text-gray-400">
            {orders.length === 0
              ? 'No orders found. Create an order to get started.'
              : 'No orders match your search.'}
          </div>
        )}
      </div>

{/* Dialog */}
      <Dialog {...dialog} onClose={closeDialog} />
    </div>
  );
}
